import TimecardHistoryButton from 'components/buttons/TimecardHistoryButton';
import TimecardTemplateButton from 'components/buttons/TimecardTemplateButton';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { FeatureFlag } from 'types/features.ts';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { DescriptionImprove } from '../form/DescriptionImprove';
import DescriptionLanguageCheck from '../form/DescriptionLanguageCheck';
import TimecardLanguageLabel from '../labels/TimecardLanguageLabel';
import { TimecardFormState } from './useTimecardForm';

/** History, then Templates when they are drawn. */
const useDescriptionButtonCount = () => 1 + Number(useIsFeatureEnabled(FeatureFlag.TIMECARD_TEMPLATES));

/**
 * On the record page, the box at the end of the description's row: the form's History and Template buttons, the
 * read row's copy icon. It has one width in both, a button and a gap for each button the form draws, so the text
 * beside it is as wide read as edited and wraps onto the same lines.
 */
export const useDescriptionSideClass = () =>
    cn('flex flex-none items-center justify-end gap-2', ['w-8', 'w-18'][useDescriptionButtonCount() - 1]);

/** The description, with its history, templates and AI improvement, and the internal notes. */
export const DescriptionFields = ({ form }: { form: TimecardFormState }): JSX.Element => {
    const { control, getValues, setValue, setFocus, subscribe } = form.methods;
    const {
        currentMatter,
        classifiers,
        handleTimecardHistoryCopy,
        handleTimecardTemplateSelect,
        isTimecardNotesEnabled,
    } = form;
    // On the record page the description and its buttons are one row of the card's grid, as tall as the read
    // row: the buttons sit side by side beside the text's box, so the text starts at one line there too, and
    // the language chip does not grow the label's line. The label has the whole row over them, as the read
    // label has: the row is a grid of the field's own parts, the label across it and the buttons beside the text.
    const inSection = useInRecordSection();
    const via = inSection ? 'record' : 'form';
    const sideClass = useDescriptionSideClass();

    return (
        <>
            {/* The description is always required, so in the create dialog it is in the Required section. A
                block rather than the field alone: the history and template buttons beside it and the AI's row and
                answer below it go there with it. */}
            <CreateDialogRequiredBlock when>
                <DescriptionLanguageCheck
                    getValues={getValues}
                    setValue={setValue}
                    subscribe={subscribe}
                    timecardLanguageCode={currentMatter?.timecardLanguageCode}
                    via={via}
                >
                    {(isLanguageMismatch, checkDescriptionLanguage, aiEdit) => (
                        <DescriptionImprove
                            control={control}
                            getValues={getValues}
                            setFocus={setFocus}
                            aiEdit={aiEdit}
                            isMismatch={isLanguageMismatch}
                            checkDescription={checkDescriptionLanguage}
                            matterId={currentMatter?.id}
                            timecardLanguageCode={currentMatter?.timecardLanguageCode}
                            via={via}
                        >
                            {(improveRow, improveBox, rulesButton) => {
                                const field = (
                                    <TextAreaInput
                                        wrapperClassName={
                                            inSection ? 'contents [&>div:first-child]:col-span-full' : undefined
                                        }
                                        name="description"
                                        required
                                        control={control}
                                        onBlur={checkDescriptionLanguage}
                                        label="timecards.form.description"
                                        tooltip={rulesButton ?? undefined}
                                        // As tall as the button column beside it.
                                        minRows={3}
                                        rightLabelComponent={
                                            currentMatter?.timecardLanguageCode ? (
                                                <div className={inSection ? '-my-1' : undefined}>
                                                    <TimecardLanguageLabel
                                                        classifierData={classifiers?.['LANGUAGE']}
                                                        timecardLanguageTypeCode={currentMatter.timecardLanguageCode}
                                                        type={isLanguageMismatch ? 'warning' : 'neutral'}
                                                    />
                                                </div>
                                            ) : null
                                        }
                                    />
                                );
                                const sideButtons = (
                                    <>
                                        <TimecardHistoryButton
                                            matterId={currentMatter?.id}
                                            handleTimecardCopy={handleTimecardHistoryCopy}
                                            className={inSection ? 'mt-0' : 'mt-7.5'}
                                        />
                                        <FeatureFlagged action="show" flag={FeatureFlag.TIMECARD_TEMPLATES}>
                                            <TimecardTemplateButton
                                                matterId={currentMatter?.id}
                                                matterLanguageCode={currentMatter?.timecardLanguageCode}
                                                onSelect={handleTimecardTemplateSelect}
                                            />
                                        </FeatureFlagged>
                                    </>
                                );
                                // The row and the box are as wide as the text, not under the buttons beside it.
                                return inSection ? (
                                    <div className="form-field col-span-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-2">
                                        {field}
                                        <div className={cn(sideClass, 'col-start-2 row-start-2 mt-1 h-9')}>
                                            {sideButtons}
                                        </div>
                                        <div className="col-start-1">
                                            {improveRow}
                                            {improveBox}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex gap-2">
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            {field}
                                            {improveRow}
                                            {improveBox}
                                        </div>
                                        <div className="flex flex-col gap-2">{sideButtons}</div>
                                    </div>
                                );
                            }}
                        </DescriptionImprove>
                    )}
                </DescriptionLanguageCheck>
            </CreateDialogRequiredBlock>
            {isTimecardNotesEnabled && (
                <TextAreaInput name="notes" control={control} label="timecards.form.notes" maxLength={5000} />
            )}
        </>
    );
};
